import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Module-level cache + dedup for /cart.js fetches. A single page may render many
 * <swatch-add-to-cart> elements (PDP popups, swatch library cards) — they all share
 * one in-flight request and the most-recent count.
 *
 * @type {Promise<number | null> | null}
 */
let inFlightCountPromise = null;
/** @type {number | null} */
let cachedSwatchCount = null;

async function fetchSwatchCount() {
  if (inFlightCountPromise) return inFlightCountPromise;
  inFlightCountPromise = (async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        console.warn('[swatch-add-to-cart] /cart.js fetch failed:', response.status);
        return null;
      }
      const cart = await response.json();
      const count = cart.items
        .filter((item) => item.product_type === 'Swatch')
        .reduce((sum, item) => sum + item.quantity, 0);
      cachedSwatchCount = count;
      return count;
    } catch (error) {
      console.error('[swatch-add-to-cart] fetch error', error);
      return null;
    } finally {
      inFlightCountPromise = null;
    }
  })();
  return inFlightCountPromise;
}

/**
 * A custom element that adds a swatch product's variant to the cart.
 * Used inside swatch info popups and dialogs to let customers order a
 * fabric/material sample without leaving the parent product page.
 *
 * Pre-disables itself when the cart already contains the configured limit
 * of swatches, and re-evaluates whenever the cart updates.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<Refs>}
 */
class SwatchAddToCart extends Component {
  requiredRefs = ['button'];

  /** @type {number | undefined} */
  #resetTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.refs.button.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#updateLimitState);
    this.#updateLimitState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.button.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#updateLimitState);
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  /** @returns {number | null} */
  #getLimit() {
    const attr = parseInt(this.dataset.swatchLimit || '', 10);
    if (Number.isFinite(attr) && attr > 0) return attr;
    const themeLimit = Theme.swatchLimit;
    if (typeof themeLimit === 'number' && themeLimit > 0) return themeLimit;
    return null;
  }

  /**
   * Refreshes the cart swatch count and toggles the limit state accordingly.
   * Triggered on connect and on every cart update.
   */
  #updateLimitState = async () => {
    const limit = this.#getLimit();
    if (!limit) return;
    const count = await fetchSwatchCount();
    if (count === null) return;
    if (count >= limit) {
      this.dataset.state = 'limit';
      this.refs.button.setAttribute('aria-disabled', 'true');
    } else if (this.dataset.state === 'limit') {
      delete this.dataset.state;
      this.refs.button.removeAttribute('aria-disabled');
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const variantId = this.dataset.variantId;
    if (!variantId || this.dataset.state === 'loading' || this.dataset.state === 'limit') return;

    const limit = this.#getLimit();
    if (limit && cachedSwatchCount !== null && cachedSwatchCount >= limit) {
      this.dataset.state = 'limit';
      this.refs.button.setAttribute('aria-disabled', 'true');
      this.dispatchEvent(
        new CartErrorEvent(this.id, `Limit of ${limit} swatches per order reached.`, '', [])
      );
      return;
    }

    this.dataset.state = 'loading';
    this.refs.button.setAttribute('aria-busy', 'true');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    const cfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'text/html' },
    })
      .then((response) => response.json())
      .then((response) => {
        this.refs.button.removeAttribute('aria-busy');

        if (response.status) {
          this.dataset.state = 'error';
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );
          this.#scheduleReset();
          return;
        }

        this.dataset.state = 'added';
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'swatch-add-to-cart',
            itemCount: 1,
            productId: this.dataset.productId,
            variantId,
            sections: response.sections,
          })
        );
        this.#scheduleReset();
      })
      .catch((error) => {
        this.refs.button.removeAttribute('aria-busy');
        this.dataset.state = 'error';
        this.#scheduleReset();
        console.error(error);
      });
  };

  /**
   * Auto-clear transient states (added/error). The 'limit' state is sticky and
   * only clears via a cart update bringing the count back below the limit.
   */
  #scheduleReset() {
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = window.setTimeout(() => {
      if (this.dataset.state !== 'limit') delete this.dataset.state;
    }, 2000);
  }
}

if (!customElements.get('swatch-add-to-cart')) {
  customElements.define('swatch-add-to-cart', SwatchAddToCart);
}
